import React, { useEffect, useState, useMemo } from "react";
import { Loader, Search } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import ErrorDisplay from "../components/common/error/ErrorDisplay";
import { AILoadingText } from "../components/common/AILoadingText";
import { useTenant } from "../contexts/TenantContext";
import {
  useAuthUser,
  useAuthLoading,
  useIsAuthenticated,
  useHasMissingContact,
  useFirebaseUser,
} from "@/hooks/auth/useAuth";
import { analyticsService } from "../services/analyticsService";
import PropertySqueezeOverlay from "../components/property/valuation/PropertySqueezeOverlay";
import { PropertyValuationData } from "../types/valuation";
import { getPropertyValuation } from "../api/valuation";
import { createValuationRecord } from "../api/valuationRecords";
import styles from "../components/property/valuation/ValuationPage.module.css";
import ErrorBoundary from "../components/error/ErrorBoundary";
import {
  PropertyHeader,
  QuickStats,
  AnalysisSection,
  AdditionalAnalysis,
  ComparableProperties,
} from "../components/property/valuation/components";

const transformValue = (value: any): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]+/g, ""));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const ValuationContent: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const { config, isLoading: tenantLoading } = useTenant();
  const user = useAuthUser();
  const authLoading = useAuthLoading();
  const isAuthenticated = useIsAuthenticated();
  const hasMissingContact = useHasMissingContact();
  const firebaseUser = useFirebaseUser();

  const [loading, setLoading] = useState(true);
  const [showSqueezeOverlay, setShowSqueezeOverlay] = useState(false);
  const [error, setError] = useState<{
    message: string;
    code?: string;
    details?: string;
  } | null>(null);
  const [propertyData, setPropertyData] =
    useState<PropertyValuationData | null>(null);

  // Handle authentication state changes
  useEffect(() => {
    // Don't make any decisions while either loading state is active
    if (loading || authLoading) {
      return;
    }

    // Only show squeeze overlay when we're certain user is not authenticated
    if (!isAuthenticated) {
      setShowSqueezeOverlay(true);
    } else {
      setShowSqueezeOverlay(false);
    }
  }, [
    loading,
    isAuthenticated,
    authLoading,
    firebaseUser,
    user,
    showSqueezeOverlay,
  ]);

  const colors = useMemo(
    () =>
      config?.branding?.colors || {
        primary: "#000000",
        secondary: "#ffffff",
        accent: "#28a745",
        background: "#ffffff",
        text: "#202124",
      },
    [config?.branding?.colors]
  );

  useEffect(() => {
    if (tenantLoading || !config) return;

    // Track page view
    analyticsService.trackEvent({
      orgId: config?.id || "default",
      tenantId: config?.id || "default",
      eventType: "pageview",
      category: "Valuation",
      action: "View",
      isAuthenticated: !!user,
      userType: user ? "client" : "visitor",
      data: {
        path: window.location.pathname,
        propertyId: listingId,
        tenantName: config?.name || "default",
      },
    });

    const fetchValuationData = async () => {
      if (!listingId) {
        setError({
          message: "Listing ID is required",
          code: "MISSING_ID",
        });
        return;
      }

      try {
        const data = await getPropertyValuation(listingId);

        // Handle missing data
        if (!data || !data.estimatedValue) {
          setError({
            message:
              "Oops! This property seems to be playing hide and seek. Let's find another one!",
          });
          return;
        }

        setPropertyData(data);

        // Save valuation record for authenticated users
        if (user && firebaseUser) {
          try {
            await createValuationRecord({
              userId: firebaseUser.uid,
              propertyDetails: data,
              organizationId: config.id,
              sourceUrl: window.location.href,
            });
          } catch (error) {
            console.error("Error saving valuation record:", error);
            // Don't block the user experience if saving fails
          }
        }

        // Track successful valuation load
        if (config) {
          analyticsService.trackEvent({
            orgId: config?.id || "default",
            tenantId: config?.id || "default",
            eventType: "interaction",
            category: "Valuation",
            action: "Load Success",
            isAuthenticated: !!user,
            userType: user ? "client" : "visitor",
            data: {
              propertyId: listingId,
              estimatedValue: data.estimatedValue,
              lastSalePrice: data.lastSalePrice,
              address: data.propertyInfo?.address?.address,
              city: data.propertyInfo?.address?.city,
              state: data.propertyInfo?.address?.state,
              zipCode: data.propertyInfo?.address?.zip,
              propertyType: data.propertyType,
              bedrooms: data.propertyInfo?.bedrooms,
              bathrooms: data.propertyInfo?.bathrooms,
              squareFeet: data.propertyInfo?.livingSquareFeet,
              yearBuilt: data.propertyInfo?.yearBuilt,
              tenantName: config?.name || "default",
            },
          });
        }
      } catch (err) {
        // Track error
        if (config) {
          analyticsService.trackEvent({
            orgId: config?.id || "default",
            tenantId: config?.id || "default",
            eventType: "error",
            category: "Valuation",
            action: "Load Error",
            isAuthenticated: !!user,
            userType: user ? "client" : "visitor",
            data: {
              propertyId: listingId,
              error: err instanceof Error ? err.message : "Unknown error",
              tenantName: config?.name || "default",
            },
          });
        }

        // Show user-friendly error message regardless of error type
        setError({
          message:
            "Oops! This property seems to be playing hide and seek. Let's find another one!",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchValuationData();
  }, [listingId, tenantLoading]);

  const metrics = useMemo(() => {
    if (!propertyData) {
      return {
        valueChangePercent: "0",
        avgCompPricePerSqft: 0,
        avgDaysOnMarket: 0,
        totalComps: 0,
        recentSales: 0,
      };
    }

    // Get comps with valid prices and square footage
    const validComps = (propertyData.comps || []).filter((comp) => {
      const price = comp.mlsSoldPrice || transformValue(comp.estimatedValue);
      const sqft = transformValue(comp.squareFeet);
      return price > 0 && sqft > 0;
    });

    // Calculate average price per square foot
    const avgCompPricePerSqft =
      validComps.length > 0
        ? validComps.reduce((sum, comp) => {
            const price =
              comp.mlsSoldPrice || transformValue(comp.estimatedValue);
            const sqft = transformValue(comp.squareFeet);
            return sum + price / sqft;
          }, 0) / validComps.length
        : 0;

    // Calculate average days on market
    const validDaysOnMarket = validComps
      .map((comp) => transformValue(comp.mlsDaysOnMarket))
      .filter((days) => days > 0);
    const avgDaysOnMarket =
      validDaysOnMarket.length > 0
        ? Math.round(
            validDaysOnMarket.reduce((a, b) => a + b, 0) /
              validDaysOnMarket.length
          )
        : 0;

    // Calculate value change percent
    const currentValue = transformValue(propertyData.estimatedValue);
    const lastSalePrice = transformValue(propertyData.lastSalePrice);
    const valueChangePercent =
      lastSalePrice > 0
        ? (((currentValue - lastSalePrice) / lastSalePrice) * 100).toFixed(1)
        : "0";

    return {
      valueChangePercent,
      avgCompPricePerSqft,
      avgDaysOnMarket,
      totalComps: propertyData.comps?.length || 0,
      recentSales: validComps.length,
    };
  }, [propertyData]);

  const capRate = useMemo(() => {
    if (
      !propertyData?.demographics?.suggestedRent ||
      !propertyData?.estimatedValue
    ) {
      return "0";
    }

    const annualRent =
      transformValue(propertyData.demographics.suggestedRent) * 12;
    const effectiveGrossIncome = annualRent * 0.85; // Assuming 15% vacancy/expenses
    const propertyValue = transformValue(propertyData.estimatedValue);

    return ((effectiveGrossIncome / propertyValue) * 100).toFixed(1);
  }, [propertyData]);

  // Show AI loading text while either property data or auth is loading
  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <AILoadingText type="valuation" style={{ color: colors.primary }} />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorDisplay
        message={error.message}
        colors={colors}
        buttonText="Search Again"
        buttonIcon={<Search className="w-4 h-4 mr-2" />}
        onButtonClick={() => navigate("/valuation")}
      />
    );
  }

  if (!propertyData) {
    return (
      <ErrorDisplay
        message="Property Not Found"
        colors={colors}
        buttonText="Search Again"
        buttonIcon={<Search className="w-4 h-4 mr-2" />}
        onButtonClick={() => navigate("/valuation")}
      />
    );
  }

  return (
    <div className="relative">
      <div className={`${!isAuthenticated ? "blur-md" : ""}`}>
        <div className={styles.fadeIn}>
          <PropertyHeader
            propertyData={propertyData}
            colors={colors}
            metrics={metrics}
            onNavigateBack={() => navigate(-1)}
          />

          <div
            className="min-h-screen"
            style={{ backgroundColor: `${colors.background}10` }}
          >
            <div className="w-full lg:container mx-auto px-4 xl:px-6 py-8">
              <QuickStats
                propertyData={propertyData}
                colors={colors}
                metrics={metrics}
                capRate={capRate}
              />

              <AnalysisSection
                propertyData={propertyData}
                transformValue={transformValue}
              />

              <AdditionalAnalysis
                propertyData={propertyData}
                transformValue={transformValue}
              />

              <ComparableProperties
                propertyData={propertyData}
                colors={colors}
                transformValue={transformValue}
              />
            </div>
          </div>
        </div>
      </div>
      {showSqueezeOverlay && propertyData && (
        <PropertySqueezeOverlay
          onClose={() => navigate("/valuation")}
          onSuccess={() => setShowSqueezeOverlay(false)}
          propertyDetails={propertyData}
          hasMissingContact={hasMissingContact}
          closeOnlyModal={true}
        />
      )}
    </div>
  );
};

const ValuationPage: React.FC = () => {
  return (
    <ErrorBoundary>
      <ValuationContent />
    </ErrorBoundary>
  );
};

export default ValuationPage;
